import { TimeView } from '@mui/x-date-pickers';
import { useEffect, useState } from 'react';
import { TeachClassSubjects } from '@protos/user_management/ums.login.apis_pb';

// ------------------------------------------------------------
// ------------------------------------------------------------

const buildDateFromValues = (dateValue: string, timeValue: string) => {
  if (!dateValue || !timeValue) return null;
  // Take only the date portion (ignore the UTC shift)
  const [year, month, day] = dateValue.split('T')[0].split('-').map(Number);
  const [hours, minutes] = timeValue
    .split(':')
    .map((v) => parseInt(v.trim(), 10));

  return new Date(year, month - 1, day, hours, minutes); // local date/time
};

// ------------------------------------------------------------

interface IDisableStartTime {
  value: Date;
  view: TimeView;
  startTimeValues: { timeValue: string; dateValue: string };
}
export const disableStartTime = (props: IDisableStartTime) => {
  // values of time that are behind current time will be disabled
  const { value, view, startTimeValues } = props;
  const now = new Date();
  const selectedDate = buildDateFromValues(
    startTimeValues.dateValue,
    startTimeValues.timeValue
  );
  const isToday =
    selectedDate && selectedDate.toDateString() === now.toDateString();

  if (!isToday) return false;
  if (view === 'hours') {
    return value.getHours() < now.getHours();
  }
  if (view === 'minutes') {
    const selectedHour = parseInt(startTimeValues.timeValue.split(':')[0], 10);

    return (
      selectedHour === now.getHours() && value.getMinutes() < now.getMinutes()
    );
  }
  if (view === 'seconds') {
    return false; // no restriction on seconds
  }
  return false;
};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

interface IDisableEndTime extends IDisableStartTime {
  endTimeValues: { timeValue: string; dateValue: string };
}
export const disableEndTime = (props: IDisableEndTime): boolean => {
  // values of time that are behind current time and start time will be disabled
  const { value, view, startTimeValues, endTimeValues } = props;
  const now = new Date();
  const selectedEndDate = buildDateFromValues(
    endTimeValues.dateValue,
    endTimeValues.timeValue
  );
  const selectedStartDate = buildDateFromValues(
    startTimeValues.dateValue,
    startTimeValues.timeValue
  );

  // If no end date, allow all times
  if (!selectedEndDate) return false;

  const isToday = selectedEndDate.toDateString() === now.toDateString();
  // If not today → only enforce after start time if same date as start
  if (!isToday) {
    if (
      selectedStartDate &&
      selectedEndDate.toDateString() === selectedStartDate.toDateString()
    ) {
      if (view === 'hours') {
        return value.getHours() < selectedStartDate.getHours();
      }
      if (view === 'minutes') {
        return (
          value.getHours() === selectedStartDate.getHours() &&
          value.getMinutes() < selectedStartDate.getMinutes()
        );
      }
    }
    return false;
  }

  // If today → block past times + times before start
  if (view === 'hours') {
    return (
      value.getHours() < now.getHours() ||
      (!!selectedStartDate && value.getHours() < selectedStartDate.getHours())
    );
  }

  if (view === 'minutes') {
    const sameHourAsNow = value.getHours() === now.getHours();
    const sameHourAsStart =
      !!selectedStartDate && value.getHours() === selectedStartDate.getHours();

    return (
      (sameHourAsNow && value.getMinutes() < now.getMinutes()) ||
      (sameHourAsStart && value.getMinutes() < selectedStartDate.getMinutes())
    );
  }

  if (view === 'seconds') {
    return false;
  }

  return false;
};

// ------------------------------------------------------------
// ------------------------------------------------------------

function formatTime(timeValue: string) {
  // Remove spaces and ensure two digits for hours and minutes
  const [hoursRaw, minutesRaw] = timeValue.split(':');
  const hours = hoursRaw.trim().padStart(2, '0');
  const minutes = (minutesRaw || '0').trim().padStart(2, '0');
  return `${hours}:${minutes}`;
}

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const isEarlier = (a: Date, b: Date) =>
  a.getHours() < b.getHours() ||
  (a.getHours() === b.getHours() && a.getMinutes() < b.getMinutes());

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const isEarlierOrSame = (a: Date, b: Date) =>
  a.getHours() < b.getHours() ||
  (a.getHours() === b.getHours() && a.getMinutes() <= b.getMinutes());

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

interface IWrongStartTime {
  startTimeValues: { timeValue: string; dateValue: string };
}
export const wrongStartTime = ({ startTimeValues }: IWrongStartTime) => {
  const now = new Date();
  now.setSeconds(0, 0);
  const selectedDate = new Date(
    `${startTimeValues.dateValue.split('T')[0]}T${formatTime(
      startTimeValues.timeValue
    )}`
  );
  selectedDate.setSeconds(0, 0);

  // day-only comparison for date rule
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sd = new Date(`${startTimeValues.dateValue.split('T')[0]}`); // incase .timeValue gives NaN
  const onlyStartDate = new Date(sd.getFullYear(), sd.getMonth(), sd.getDate());
  const wrongDate = onlyStartDate < today;

  // only compare time-of-day if it's the same calendar day
  const isSameDay = selectedDate.toDateString() === now.toDateString();
  const wrongTime = isSameDay && isEarlier(selectedDate, now);
  const [hours, minutes] = startTimeValues.timeValue
    .split(':')
    .map((v) => parseInt(v.trim(), 10));
  const AmOrPmNotPicked = isNaN(hours) && isNaN(minutes);

  return {
    wrongDate: wrongDate || !startTimeValues.dateValue,
    wrongTime: wrongTime || AmOrPmNotPicked || !startTimeValues.dateValue,
  };
};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

interface IWrongEndTime {
  startTimeValues: { timeValue: string; dateValue: string };
  endTimeValues: { timeValue: string; dateValue: string };
}
export const wrongEndTime = ({
  startTimeValues,
  endTimeValues,
}: IWrongEndTime) => {
  const now = new Date();
  now.setSeconds(0, 0);

  const startingPoint = new Date(
    `${startTimeValues.dateValue.split('T')[0]}T${formatTime(
      startTimeValues.timeValue
    )}`
  );
  startingPoint.setSeconds(0, 0);
  const endingPoint = new Date(
    `${endTimeValues.dateValue.split('T')[0]}T${formatTime(
      endTimeValues.timeValue
    )}`
  );
  endingPoint.setSeconds(0, 0);

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sd = new Date(`${startTimeValues.dateValue.split('T')[0]}`);
  const startDay = new Date(sd.getFullYear(), sd.getMonth(), sd.getDate());
  const ed = new Date(`${endTimeValues.dateValue.split('T')[0]}`);
  const endDay = new Date(ed.getFullYear(), ed.getMonth(), ed.getDate());
  const wrongDate = endDay < today || endDay < startDay;

  const sameDayAsNow = endingPoint.toDateString() === now.toDateString();
  const sameDayAsStart =
    endingPoint.toDateString() === startingPoint.toDateString();
  const wrongTime =
    (sameDayAsNow && isEarlierOrSame(endingPoint, now)) ||
    (sameDayAsStart && isEarlierOrSame(endingPoint, startingPoint));
  const [hours, minutes] = endTimeValues.timeValue
    .split(':')
    .map((v) => parseInt(v.trim(), 10));
  const AmOrPmNotPicked = isNaN(hours) && isNaN(minutes);

  return {
    wrongDate: wrongDate || !endTimeValues.dateValue,
    wrongTime: wrongTime || AmOrPmNotPicked || !endTimeValues.dateValue,
  };
};

// ------------------------------------------------------------
// ------------------------------------------------------------

// to rerender if time has passed
export const useCurrentMinute = () => {
  const [minute, setMinute] = useState(new Date().getMinutes());
  useEffect(() => {
    const interval = setInterval(() => {
      const newMinute = new Date().getMinutes();
      setMinute((prevMinute) =>
        prevMinute !== newMinute ? newMinute : prevMinute
      );
    }, 1000); // check every second

    return () => clearInterval(interval);
  }, []);

  return minute;
};

// ------------------------------------------------------------
// ------------------------------------------------------------

interface SubjectListItem {
  subjectName: string;
  id: number;
}

/**
 * Builds array of { subjectName, id } for an assessment card using
 * card.subjectIds, card.classId and subject names from teachClassSubjects.
 */
export function getSubjectListForAssessmentCard(
  card: { subjectIds?: number[]; classId?: number },
  teachClassSubjects?: TeachClassSubjects[]
): SubjectListItem[] {
  const subjectIds = card.subjectIds ?? [];
  const classId = card.classId;
  if (!subjectIds.length || classId == null || !teachClassSubjects?.length) {
    return [];
  }
  const classInfo = teachClassSubjects.find(
    (c) => (c as { classId?: number }).classId === classId
  );
  if (!classInfo?.subjects?.length) return [];
  const list: SubjectListItem[] = [];
  for (const subjectId of subjectIds) {
    const subject = classInfo.subjects.find(
      (s) => (s as { subjectId?: number }).subjectId === subjectId
    );
    const name =
      (subject as { subjectName?: string } | undefined)?.subjectName ?? '';
    list.push({ subjectName: name, id: subjectId });
  }
  return list;
}

// ------------------------------------------------------------
// ------------------------------------------------------------
